import React from "react";
import { AlertTriangle, Inbox } from "lucide-react";

export const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export const num = (n) => Number(n || 0).toLocaleString("en-IN");

export const bytes = (b) => {
  const v = Number(b || 0);
  if (v < 1024) return `${v} B`;
  if (v < 1024 ** 2) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 ** 3) return `${(v / 1024 ** 2).toFixed(1)} MB`;
  return `${(v / 1024 ** 3).toFixed(2)} GB`;
};

export const day = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

export const dayTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";

export const Kpi = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-lg border border-border bg-white p-5 shadow-sm">
    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-2 font-display text-3xl text-navy">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export const Panel = ({ title, action, children, testid }) => (
  <section data-testid={testid} className="rounded-lg border border-border bg-white shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
      <h2 className="font-display text-xl text-navy">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

export const Skeleton = ({ rows = 4, testid = "owner-loading" }) => (
  <div data-testid={testid} className="space-y-3 p-5">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-10 rounded bg-secondary" />
    ))}
  </div>
);

export const ErrorState = ({ message, onRetry, testid = "owner-error" }) => (
  <div data-testid={testid} className="flex flex-col items-center gap-3 px-5 py-12 text-center">
    <AlertTriangle className="h-8 w-8 text-destructive" />
    <p className="text-sm text-muted-foreground">{message}</p>
    {onRetry && (
      <button type="button" onClick={onRetry} data-testid="owner-retry-btn"
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-navy hover:bg-secondary">
        Try again
      </button>
    )}
  </div>
);

export const Empty = ({ message, testid = "owner-empty" }) => (
  <div data-testid={testid} className="flex flex-col items-center gap-3 px-5 py-12 text-center">
    <Inbox className="h-8 w-8 text-muted-foreground" />
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

export const Pager = ({ page, pages, total, onPage, testid = "owner-pager" }) => (
  <div data-testid={testid} className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
    <p className="text-xs text-muted-foreground">
      Page {page} of {Math.max(pages, 1)} · {num(total)} total
    </p>
    <div className="flex gap-2">
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}
              data-testid="owner-prev-page"
              className="rounded-md border border-border px-3 py-1.5 text-sm text-navy disabled:opacity-40">
        Previous
      </button>
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}
              data-testid="owner-next-page"
              className="rounded-md border border-border px-3 py-1.5 text-sm text-navy disabled:opacity-40">
        Next
      </button>
    </div>
  </div>
);

export const Badge = ({ children }) => (
  <span className="inline-flex rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[11px] font-medium text-gold-dark">
    {children || "—"}
  </span>
);

export const selectClass =
  "rounded-md border border-border bg-white px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-navy/20";
